<template>
  <div class="column-settings">
    <el-tooltip :content="buttonTooltip" placement="top" :open-delay="300">
      <el-button
        size="small"
        icon="el-icon-setting"
        class="btn-setting-icon"
        :aria-label="buttonTooltip"
        @click="openDialog"
      ></el-button>
    </el-tooltip>

    <el-dialog
      :title="title"
      :visible.sync="dialogVisible"
      :width="dialogWidth"
      :close-on-click-modal="false"
      center
      class="column-settings-dialog"
      @closed="resetSelection"
    >
      <div class="dialog-body-desc">
        勾选需要在表格中显示的列。
        <template v-if="fixedColumnLabels.length">
          其中 <b>{{ fixedColumnLabels.join('、') }}</b> 为核心常驻字段。
        </template>
        <span v-if="minRequired > 0" class="limit-warning">
          （注意：最少需要选择 {{ minRequired }} 个其他自定义展示列）
        </span>
      </div>

      <div class="checkbox-container">
        <template v-if="fixedColumnLabels.length">
          <div class="dialog-column-group-title">
            <i class="el-icon-info"></i> 核心常驻字段
          </div>
          <div class="fixed-columns-row">
            <div v-for="item in fixedColumnLabels" :key="item" class="fixed-column-card">{{ item }}</div>
          </div>
          <div class="column-divider"></div>
        </template>

        <div class="dialog-column-group-title optional-title">
          <i class="el-icon-menu"></i> 可选显示列
        </div>
        <el-checkbox-group v-model="selectedColumns" class="dialog-checkbox-group">
          <el-checkbox
            v-for="item in normalizedColumns"
            :key="item.prop"
            :label="item.prop"
            :disabled="item.disabled || isMinimumColumn(item.prop)"
          >
            {{ item.label }}
          </el-checkbox>
        </el-checkbox-group>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" type="warning" plain class="btn-dialog-action" @click="handleRestore">恢复默认</el-button>
        <div class="right-buttons-group">
          <el-button size="small" class="btn-dialog-action" @click="handleCancel">取 消</el-button>
          <el-button size="small" type="primary" class="btn-dialog-action" @click="handleConfirm">确 定</el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ColumnSettings',
  props: {
    columns: {
      type: Array,
      required: true
    },
    value: {
      type: Array,
      required: true
    },
    defaultColumns: {
      type: Array,
      default: () => []
    },
    fixedColumns: {
      type: Array,
      default: () => []
    },
    minSelected: {
      type: Number,
      default: 0
    },
    title: {
      type: String,
      default: '自定义列表字段'
    },
    buttonTooltip: {
      type: String,
      default: '自定义列表字段'
    },
    dialogWidth: {
      type: String,
      default: '680px'
    }
  },
  data () {
    return {
      dialogVisible: false,
      selectedColumns: [...this.value]
    }
  },
  computed: {
    normalizedColumns () {
      return this.columns.map(item => ({
        prop: item.prop || item.value,
        label: item.label,
        disabled: Boolean(item.disabled)
      })).filter(item => item.prop)
    },
    fixedColumnLabels () {
      return this.fixedColumns.map(item => typeof item === 'string' ? item : item.label).filter(Boolean)
    },
    minRequired () {
      return Math.min(Math.max(this.minSelected, 0), this.normalizedColumns.length)
    },
    effectiveDefaultColumns () {
      const validProps = this.normalizedColumns.map(item => item.prop)
      const normalizedDefaults = this.defaultColumns.filter(prop => validProps.includes(prop))
      return normalizedDefaults.length >= this.minRequired
        ? normalizedDefaults
        : validProps.slice(0, this.minRequired)
    }
  },
  watch: {
    value (newVal) {
      if (!this.dialogVisible) {
        this.selectedColumns = [...newVal]
      }
    }
  },
  methods: {
    openDialog () {
      this.selectedColumns = this.value.length >= this.minRequired
        ? [...this.value]
        : [...this.effectiveDefaultColumns]
      this.dialogVisible = true
    },
    resetSelection () {
      this.selectedColumns = [...this.value]
    },
    isMinimumColumn (prop) {
      return this.minRequired > 0 &&
        this.selectedColumns.length <= this.minRequired &&
        this.selectedColumns.includes(prop)
    },
    handleRestore () {
      this.selectedColumns = [...this.effectiveDefaultColumns]
      this.$message.info('已恢复为默认展示字段')
    },
    handleCancel () {
      this.dialogVisible = false
    },
    handleConfirm () {
      if (this.selectedColumns.length < this.minRequired) {
        this.$message.warning(`最少需要展示 ${this.minRequired} 列字段，已自动恢复默认展示`)
        this.selectedColumns = [...this.effectiveDefaultColumns]
      }
      const value = [...this.selectedColumns]
      this.$emit('input', value)
      this.$emit('change', value)
      this.dialogVisible = false
      this.$message.success('展示列配置修改成功')
    }
  }
}
</script>

<style scoped>
.column-settings {
  display: inline-flex;
  flex-shrink: 0;
}

.btn-setting-icon {
  width: 32px !important;
  height: 32px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin: 0 !important;
  padding: 0 !important;
  border-color: var(--border-color) !important;
  border-radius: 8px !important;
  background-color: var(--bg-card) !important;
  color: var(--text-muted) !important;
}

.btn-setting-icon:hover,
.btn-setting-icon:focus {
  border-color: var(--border-hover) !important;
  background-color: var(--bg-page) !important;
  color: var(--text-main) !important;
}

.column-settings-dialog :deep(.el-dialog) {
  max-width: calc(100vw - 32px);
  border-radius: 12px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.column-settings-dialog :deep(.el-dialog__header) {
  padding: 20px 24px 16px !important;
  border-bottom: 1px solid var(--border-light);
}

.column-settings-dialog :deep(.el-dialog__title) {
  color: var(--text-main);
  font-size: 16px;
  font-weight: 700;
}

.column-settings-dialog :deep(.el-dialog__body) {
  padding: 24px;
}

.column-settings-dialog :deep(.el-dialog__footer) {
  padding: 16px 24px 20px !important;
  border-top: 1px solid var(--border-light);
  text-align: inherit !important;
}

.dialog-body-desc {
  margin: -6px 0 20px;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.6;
  text-align: left;
}

.limit-warning {
  color: #f59e0b;
  font-weight: 600;
}

.checkbox-container {
  max-height: 400px;
  overflow-y: auto;
  padding: 20px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background-color: var(--bg-page);
}

.dialog-column-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 700;
}

.dialog-column-group-title i {
  color: var(--primary-color, #2563eb);
  font-size: 14px;
}

.optional-title {
  margin-top: 0;
}

.fixed-columns-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.fixed-column-card {
  padding: 8px 16px;
  border: 1px dashed var(--border-hover);
  border-radius: 8px;
  background: var(--bg-hover);
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 600;
  user-select: none;
}

.column-divider {
  height: 1px;
  margin: 18px 0;
  background: var(--border-color);
}

.dialog-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 0;
  width: 100%;
}

.dialog-checkbox-group :deep(.el-checkbox) {
  width: calc(33.333% - 10px);
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  margin-right: 10px !important;
  margin-bottom: 6px !important;
  padding: 8px 14px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-card);
  transition: all 0.2s ease;
}

.dialog-checkbox-group :deep(.el-checkbox:hover) {
  border-color: var(--border-hover);
  background: var(--bg-page);
}

.dialog-checkbox-group :deep(.el-checkbox.is-checked) {
  border-color: rgba(37, 99, 235, 0.4);
  background: rgba(37, 99, 235, 0.03);
}

html.dark .dialog-checkbox-group :deep(.el-checkbox.is-checked) {
  border-color: rgba(59, 130, 246, 0.45) !important;
  background: rgba(59, 130, 246, 0.12) !important;
}

.dialog-checkbox-group :deep(.el-checkbox__label) {
  overflow: hidden;
  color: var(--text-main);
  font-size: 13px;
  font-weight: 500;
  text-overflow: ellipsis;
}

.btn-dialog-action {
  padding: 8px 18px !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
}

.dialog-footer,
.right-buttons-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dialog-footer {
  justify-content: space-between;
  width: 100%;
}

@media (max-width: 640px) {
  .column-settings-dialog :deep(.el-dialog__body) {
    padding: 20px 16px;
  }

  .checkbox-container {
    padding: 16px;
  }

  .dialog-checkbox-group :deep(.el-checkbox) {
    width: 100%;
    margin-right: 0 !important;
  }
}
</style>
